import React, { useEffect, useRef } from 'react';

import { FLAG_STATE, webSocketWatcherEventTarget } from '@/components/UI/WebSocketWatcher';
import { useIdleContext } from '@/renderer/utils/useIdle';

// Use a random timer 250-1750ms to avoid hammering the server all at once
const FLAG_TIMEOUT = Math.floor(250 + Math.random() * 1500);

/**
 * Records the web-delivery state of a single rendered DM receipt so the server
 * unread count drains as the member reads the thread. Mounted once per receipt
 * in the support-chat DM thread. It dispatches `directMessageReceipt/flag`
 * events that WebSocketWatcher coalesces into the bulk `POST /me/flag` write:
 *   • present (thread on screen + focused) → VISITED (7): read, drains the
 *     unread count.
 *   • backgrounded (tab hidden / window unfocused) → ACK (4): received but not
 *     read; it re-checks on a backoff and flips to VISITED the moment the
 *     member returns, without needing a mouse move.
 *
 * "Present" is keyed on visibility + focus, NOT the broad idle timer — see the
 * inline note. Exported (not inlined in DMReceiptList) so the present-vs-
 * backgrounded read-ack decision has a focused unit test, mirroring
 * release-center/components/MessageReceiptLogger.tsx.
 */
export const MessageReceiptLogger = React.memo(
  function MessageReceiptInner(messageReceipt: API.DirectMessageReceipt) {
    const { id, web, createdAt } = messageReceipt;
    const webStatus = useRef(web);

    useEffect(() => {
      webStatus.current = web;
    }, [web]);

    const idle = useIdleContext();
    // "Present" = `idle.present` (the DM thread is on screen AND focused),
    // computed once in useIdle.tsx (#2072) so this can't drift from the
    // release-center MessageReceiptLogger, the sidebar unread counter, or the
    // desktop-notification gates. Deliberately keyed on visibility+focus, NOT
    // the broad idle timer (`idle.inactive`, which is `idle || !visible ||
    // !focused`): a member reading this thread without moving the mouse trips
    // the idle timer (~10s desktop) while the conversation is very much on
    // screen and being read. Marking a receipt read must follow the same
    // "present, not merely non-idle" rule as release-center's
    // MessageReceiptLogger (#1212/#1885/#2058) — otherwise a DM that arrives
    // while the member is present-but-idle is recorded only as ACK (<
    // VISITED), understating read state.
    const isPresent = useRef(idle.present);

    useEffect(() => {
      isPresent.current = idle.present;
    }, [idle.present]);

    useEffect(() => {
      let newState = webStatus.current;
      let timer: ReturnType<typeof setTimeout> | null | undefined;
      let fn: null | undefined | (() => void);
      let nextTimeout = 15000 + Math.random() * 5000;

      if (Date.now() - Date.parse(createdAt) > 1000 * 60 * 60 * 24 * 7) {
        // Don't change status of messages a week+ old
        return;
      }

      if (newState < FLAG_STATE.VISITED) {
        // mark it as acknowledged if backgrounded, visited if present
        fn = () => {
          timer = null;
          const present = isPresent.current;

          if (
            newState <= FLAG_STATE.ACK && // not yet read
            !present
          ) {
            if (newState < FLAG_STATE.ACK) {
              webSocketWatcherEventTarget.dispatchEvent(
                new CustomEvent('ras:webSocketWatcher/flag', {
                  detail: {
                    event: `directMessageReceipt/flag`,
                    data: {
                      id: id,
                      item: messageReceipt,
                      flag: FLAG_STATE.VISITED,
                      idleFlag: FLAG_STATE.ACK,
                    },
                  },
                }),
              );

              webStatus.current = newState = FLAG_STATE.ACK;
            }
          } else if (newState < FLAG_STATE.VISITED) {
            // not yet read
            if (present) {
              fn = null;
              webSocketWatcherEventTarget.dispatchEvent(
                new CustomEvent('ras:webSocketWatcher/flag', {
                  detail: {
                    event: `directMessageReceipt/flag`,
                    data: {
                      id: id,
                      item: messageReceipt,
                      flag: FLAG_STATE.VISITED,
                    },
                  },
                }),
              );

              webStatus.current = newState = FLAG_STATE.VISITED;

              return; // return to avoid re-running read timer
            }
          }

          // Keep running the timer until they come back, so we can later flag it as VISITED
          if (fn) {
            timer = setTimeout(fn, nextTimeout); // second check is faster
            nextTimeout = 60000 + Math.random() * 30000; // third+ check is long
          }
        };

        timer = setTimeout(fn, FLAG_TIMEOUT);
      }

      if (timer && fn) {
        return () => {
          if (timer) {
            clearTimeout(timer);
            timer = null;

            if (fn) {
              fn(); // flag immediately on navigate away
              fn = null;
            }
          }
        };
      }
    }, []);

    return null;
  },
  (prev, next) => prev?.id === next?.id && prev?.web === next?.web,
);

export default MessageReceiptLogger;
